/**
 * FlowBuilder — the 2.0 flow builder.
 *
 * A fresh presentation layer built on the Phase 0 extracted hooks
 * (`useStepSchemas`, `useFlowValidation`, `useFlowVariableContext`,
 * `useFlowPersistence`) and `flowGraphOps`. It renders the flow as a clean
 * vertical step list with inline branch lanes — no drill-in navigation — per
 * the campaign-builder mockup.
 *
 * It is NOT a fork of `ListFlowBuilder`: the hard-won domain logic is shared
 * (the hooks), only the canvas/interaction is new. Step configuration reuses
 * the existing `StepConfigSidebar`; adding steps reuses `StepSelectorModal`.
 *
 * Hosts:
 * - The campaign wizard embeds it (`embedded` — suppresses the standalone
 *   header/save bar; the wizard drives saving).
 * - Eventually the standalone flow editor will swap onto it (Phase 3 —
 *   deferred). For now `ListFlowBuilder` still owns that surface.
 */
import {
	faClockRotateLeft,
	faCodeBranch,
	faCodeCompare,
	faEnvelope,
	faGears,
	faGlobePointer,
	faInbox,
	faMessage,
	faPhone,
	faPlay,
	faPlus,
	faTrashCan,
	faWrench,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, {
	forwardRef,
	useCallback,
	useEffect,
	useImperativeHandle,
	useMemo,
	useState,
} from 'react';
import { Button, LoadingSpinner, Toast } from '../..';
import { useThemeColors } from '../../providers/ThemeProvider';
import { useTranslation } from '../../providers/TranslationProvider';
import {
	useFlowPersistence,
	type FlowPersistenceOutcome,
} from '../hooks/useFlowPersistence';
import { useFlowValidation } from '../hooks/useFlowValidation';
import { useFlowVariableContext } from '../hooks/useFlowVariableContext';
import { useStepSchemas } from '../hooks/useStepSchemas';
import type { AutomationStep, StepConnection } from '../types/automation';
import {
	buildAppendConnection,
	deleteStepFromGraph,
	type BranchContextEntry,
} from '../utils/flowGraphOps';
import { isFlowValid } from '../utils/flowValidation';
import StepConfigSidebar from './StepConfigSidebar';
import StepSelectorModal from './StepSelectorModal';

// ── Props ────────────────────────────────────────────────────────────────

export interface FlowBuilderProps {
	/**
	 * The flow being edited. Only the version-tracking fields are read here;
	 * `useFlowPersistence` mutates `draftVersion` / `draftVersionId` in place on
	 * save. The standalone editor and the campaign wizard both satisfy this —
	 * neither the flow's name nor its other metadata is needed by the builder.
	 */
	flow: {
		draftVersion?: {
			steps?: Array<AutomationStep>;
			connections?: Array<StepConnection>;
		} | null;
		draftVersionId?: number;
		activeVersionId?: number;
		versions?: Array<unknown>;
	};
	flowId: number;
	apiService?: any;
	selectedVersionId?: number;
	onVersionChange?: (versionId: number) => void;
	/**
	 * When true the standalone header/save bar is suppressed — the host (e.g.
	 * the campaign wizard) drives saving via the imperative handle. Defaults to
	 * false (standalone).
	 */
	embedded?: boolean;
	/**
	 * Called after a successful draft save. Lets an embedding host react
	 * (e.g. the campaign wizard updating its "draft saved" indicator).
	 */
	onDraftSaved?: () => void;
	/**
	 * Called whenever the step count changes, with the number of non-start
	 * steps. Lets an embedding host tell whether the flow has anything in it
	 * yet (e.g. to gate a wizard's "Continue" button).
	 */
	onStepCountChange?: (count: number) => void;
	/**
	 * Called whenever the flow's validity changes — lets an embedding host
	 * gate a launch action on the flow being error-free.
	 */
	onValidityChange?: (valid: boolean) => void;
}

/** One input variable declared on the flow's start step. */
export interface FlowInputVariable {
	name: string;
	type: string;
	required: boolean;
	description?: string;
}

/**
 * Imperative handle exposed to embedding hosts. The campaign wizard calls
 * `saveAndCommit` on launch to lock the in-progress draft into a committed
 * version that the batch can pin, and reads `getInputVariables` to drive the
 * audience deduplication / field-mapping UI.
 */
export interface FlowBuilderHandle {
	saveAndCommit: () => Promise<FlowPersistenceOutcome>;
	/**
	 * Persist the in-progress draft WITHOUT committing it to a locked version
	 * — used to flush edits before a test run.
	 */
	saveDraft: () => Promise<FlowPersistenceOutcome>;
	/**
	 * Whether the builder holds edits not yet persisted. Lets an editing host
	 * skip a redundant commit when the flow was opened but never touched.
	 */
	hasUnsavedChanges: () => boolean;
	/** The flow's input variables, read live from the start step's config. */
	getInputVariables: () => Array<FlowInputVariable>;
}

// ── Step type presentation ───────────────────────────────────────────────

const STEP_ICONS: Record<string, typeof faGears> = {
	start: faPlay,
	send_email: faEnvelope,
	template: faEnvelope,
	email_ingest: faInbox,
	product_match: faCodeCompare,
	external_lookup: faGlobePointer,
	send_sms: faMessage,
	agent: faPhone,
	agent_call: faPhone,
	condition: faCodeBranch,
	parallel: faCodeBranch,
	wait_for_event: faClockRotateLeft,
	delay: faClockRotateLeft,
	action: faWrench,
};

function iconForStep(stepType: string): typeof faGears {
	return STEP_ICONS[stepType] ?? faGears;
}

/** A branch lane declared on a branch step. */
interface BranchLane {
	key: string;
	label: string;
}

/**
 * The branch lanes declared on a step (condition / parallel / wait_for_event).
 * Read from step config so lanes render even before they have any steps.
 */
function getBranchLanes(step: AutomationStep): Array<BranchLane> {
	const config = (step.config ?? {}) as Record<string, any>;
	const lanes: Array<BranchLane> = [];

	if (Array.isArray(config['conditions'])) {
		for (const condition of config['conditions']) {
			lanes.push({
				key: condition.key,
				label: condition.label ?? condition.key,
			});
		}
	}
	if (config['branches'] != null && typeof config['branches'] === 'object') {
		for (const [key, branchConfig] of Object.entries(config['branches'])) {
			lanes.push({
				key,
				label: (branchConfig as any)?.branchName ?? key,
			});
		}
	}
	if (Array.isArray(config['exitHandles'])) {
		for (const handle of config['exitHandles']) {
			lanes.push({ key: handle.id, label: handle.label ?? handle.id });
		}
	}

	return lanes;
}

// ── Component ────────────────────────────────────────────────────────────

const FlowBuilderInner: React.FC<
	FlowBuilderProps & { builderRef: React.ForwardedRef<FlowBuilderHandle> }
> = ({
	flow,
	flowId,
	apiService,
	selectedVersionId,
	onVersionChange,
	embedded = false,
	onDraftSaved,
	onStepCountChange,
	onValidityChange,
	builderRef,
}) => {
	const { t } = useTranslation();
	const colors = useThemeColors();

	const [steps, setSteps] = useState<Array<AutomationStep>>([]);
	const [connections, setConnections] = useState<Array<StepConnection>>([]);
	const [loading, setLoading] = useState(true);
	const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
	const [selectedStep, setSelectedStep] = useState<AutomationStep | null>(null);
	const [showStepConfig, setShowStepConfig] = useState(false);
	// Where a newly-added step should attach: the branch context + the steps
	// already in that scope. Captured when an "Add step" button is clicked.
	const [addTarget, setAddTarget] = useState<{
		branchContext: Array<BranchContextEntry>;
		stepsInScope: Array<AutomationStep>;
	} | null>(null);

	// ── Phase 0 hooks ────────────────────────────────────────────────────
	const {
		stepTypeSchemas,
		stepTypeLabels,
		dataLoaderSchemas,
		agentOutputSchemas,
	} = useStepSchemas(steps);
	const validationResult = useFlowValidation(steps, connections);
	const resolveStepContext = useFlowVariableContext({
		steps,
		connections,
		stepTypeSchemas,
		dataLoaderSchemas,
		agentOutputSchemas,
	});
	const persistence = useFlowPersistence({
		apiService,
		flowId,
		selectedVersionId,
		steps,
		connections,
		validationResult,
		hasUnsavedChanges,
		flow,
	});

	// Load flow data from the draft version.
	useEffect(() => {
		if (flow?.draftVersion != null) {
			setSteps(flow.draftVersion.steps ?? []);
			setConnections(flow.draftVersion.connections ?? []);
			setLoading(false);
		}
	}, [flow]);

	// Report the non-start step count so an embedding host can tell whether
	// the flow has anything in it yet.
	useEffect(() => {
		onStepCountChange?.(
			steps.filter((step) => step.stepType !== 'start').length,
		);
	}, [steps, onStepCountChange]);

	// Report flow validity so an embedding host can gate launch on it.
	useEffect(() => {
		onValidityChange?.(isFlowValid(validationResult));
	}, [validationResult, onValidityChange]);

	// Expose save+commit to an embedding host (the campaign wizard) so it can
	// lock the in-progress draft into a committed version on launch. Standalone
	// usage passes no ref and is unaffected.
	useImperativeHandle(
		builderRef,
		() => ({
			saveAndCommit: async (): Promise<FlowPersistenceOutcome> => {
				const outcome = await persistence.saveAndCommit();
				if (outcome.ok) {
					setHasUnsavedChanges(false);
				}
				return outcome;
			},
			saveDraft: async (): Promise<FlowPersistenceOutcome> => {
				const outcome = await persistence.save();
				if (outcome.ok) {
					setHasUnsavedChanges(false);
				}
				return outcome;
			},
			hasUnsavedChanges: (): boolean => hasUnsavedChanges,
			getInputVariables: (): Array<FlowInputVariable> => {
				const start = steps.find((s) => s.stepType === 'start');
				const config = (start?.config ?? {}) as {
					customFields?: Array<{
						name?: string;
						type?: string;
						required?: boolean;
						description?: string;
					}>;
				};
				return (config.customFields ?? [])
					.filter((field) => field.name != null && field.name.trim() !== '')
					.map((field) => ({
						name: field.name as string,
						type: typeof field.type === 'string' ? field.type : 'string',
						required: field.required === true,
						description: field.description,
					}));
			},
		}),
		[persistence, steps, hasUnsavedChanges],
	);

	// ── Sequence + branch derivation ─────────────────────────────────────

	/** Ordered steps reachable from `firstStepId` by following `next` connections. */
	const getSequence = useCallback(
		(firstStepId: number | null): Array<AutomationStep> => {
			const result: Array<AutomationStep> = [];
			const visited = new Set<number>();
			let currentId = firstStepId;
			while (currentId != null && !visited.has(currentId)) {
				visited.add(currentId);
				const step = steps.find((s) => s.id === currentId);
				if (step == null) {
					break;
				}
				result.push(step);
				const nextConn = connections.find(
					(c) => c.fromStepId === currentId && c.conditionKey === 'next',
				);
				currentId = nextConn?.toStepId ?? null;
			}
			return result;
		},
		[steps, connections],
	);

	/** The main flow: the sequence starting at the step after `start`. */
	const mainSequence = useMemo(() => {
		const startStep = steps.find((s) => s.stepType === 'start');
		if (startStep?.id == null) {
			return [];
		}
		const firstConn = connections.find(
			(c) => c.fromStepId === startStep.id && c.conditionKey === 'next',
		);
		return getSequence(firstConn?.toStepId ?? null);
	}, [steps, connections, getSequence]);

	const startStep = useMemo(
		() => steps.find((s) => s.stepType === 'start') ?? null,
		[steps],
	);

	// ── Handlers ─────────────────────────────────────────────────────────

	const handleEditStep = useCallback(
		(step: AutomationStep) => {
			const isSameStep =
				selectedStep != null &&
				((selectedStep.id != null && selectedStep.id === step.id) ||
					(selectedStep.internalId != null &&
						selectedStep.internalId === step.internalId));
			// Clicking the already-open step collapses its inline editor.
			if (isSameStep && showStepConfig) {
				setShowStepConfig(false);
				setSelectedStep(null);
				return;
			}
			setSelectedStep(step);
			setShowStepConfig(true);
		},
		[selectedStep, showStepConfig],
	);

	const handleDeleteStep = useCallback(
		(step: AutomationStep) => {
			if (step.stepType === 'start') {
				return;
			}
			const next = deleteStepFromGraph(step, steps, connections);
			setSteps(next.steps);
			setConnections(next.connections);
			setHasUnsavedChanges(true);
		},
		[steps, connections],
	);

	/** Open the step palette, remembering where the new step should attach. */
	const handleOpenPalette = useCallback(
		(
			branchContext: Array<BranchContextEntry>,
			stepsInScope: Array<AutomationStep>,
		) => {
			setAddTarget({ branchContext, stepsInScope });
		},
		[],
	);

	const handleStepSelected = useCallback(
		(stepType: string) => {
			if (addTarget == null) {
				return;
			}
			const newStepId = -Date.now();
			const newStep: AutomationStep = {
				id: newStepId,
				internalId: crypto.randomUUID(),
				stepType,
				stepName: stepType.charAt(0).toUpperCase() + stepType.slice(1),
				config: {},
				sortOrder: steps.length,
				// `stepType` is a plain string here; the union-typed field is
				// satisfied at runtime by the palette only emitting valid types.
			} as unknown as AutomationStep;

			setSteps((prev) => [...prev, newStep]);

			const newConnection = buildAppendConnection(
				newStepId,
				addTarget.branchContext,
				addTarget.stepsInScope,
			);
			if (newConnection != null) {
				setConnections((prev) => [...prev, newConnection]);
			}

			setAddTarget(null);
			setHasUnsavedChanges(true);
			// Open config for the new step immediately.
			setSelectedStep(newStep);
			setShowStepConfig(true);
		},
		[addTarget, steps],
	);

	/**
	 * Inline always-save: an edit in the step config panel is committed to the
	 * flow immediately, without closing the panel. `selectedStep` is the
	 * pre-edit snapshot and is intentionally never reassigned here, so the
	 * panel's Revert action can restore the step to it.
	 */
	const handleStepConfigChange = useCallback(
		(updates: { config?: any; stepName?: string }) => {
			if (selectedStep == null) {
				return;
			}
			setSteps((prev) =>
				prev.map((s) =>
					s.id === selectedStep.id
						? {
								...s,
								config: updates.config ?? s.config,
								stepName: updates.stepName ?? s.stepName,
							}
						: s,
				),
			);
			setHasUnsavedChanges(true);
		},
		[selectedStep],
	);

	/** Draft save — the only persistence action surfaced in the builder. */
	const handleSaveDraft = useCallback(async () => {
		const outcome = await persistence.save();
		if (outcome.validationError != null) {
			Toast.show({
				type: 'error',
				text1: t('Cannot save with errors'),
				text2: t(
					`Fix ${outcome.validationError.errorCount} error(s) in ${outcome.validationError.nodeCount} step(s)`,
				),
			});
			return;
		}
		if (!outcome.ok) {
			Toast.show({
				type: 'error',
				text1: t('Failed to save'),
				text2: outcome.error != null ? t(outcome.error) : undefined,
			});
			return;
		}
		Toast.show({ type: 'success', text1: t('Draft saved') });
		setHasUnsavedChanges(false);
		if (outcome.newDraftVersionId != null && onVersionChange != null) {
			onVersionChange(outcome.newDraftVersionId);
		}
		onDraftSaved?.();
	}, [persistence, t, onVersionChange, onDraftSaved]);

	// Branch context passed to the variable resolver for the config sidebar.
	// 2.0 has no drill-in, so for now the sidebar resolves against the main
	// flow; per-lane context will be threaded once branch editing lands.
	const buildStepContext = useCallback(
		(stepIdentifier: string) => resolveStepContext(stepIdentifier, []),
		[resolveStepContext],
	);

	// ── Render helpers ───────────────────────────────────────────────────

	const renderConnector = (key: string) => (
		<div
			key={key}
			className="w-px h-5 mx-auto"
			style={{ backgroundColor: colors.values.borderMedium }}
		/>
	);

	const renderAddButton = (
		key: string,
		branchContext: Array<BranchContextEntry>,
		stepsInScope: Array<AutomationStep>,
	) => (
		<div key={key} className="flex justify-center">
			<button
				type="button"
				onClick={() => handleOpenPalette(branchContext, stepsInScope)}
				className={cnAdd()}
			>
				<FontAwesomeIcon icon={faPlus} className="h-3 w-3" />
				{t('Add step')}
			</button>
		</div>
	);

	/** Render one step node. */
	const renderNode = (step: AutomationStep, isStart: boolean) => {
		const errors =
			validationResult.get(step.internalId ?? step.id?.toString() ?? '') ?? [];
		const hasErrors = errors.length > 0;
		const label =
			stepTypeLabels.get(step.stepType) ?? step.stepName ?? step.stepType;

		return (
			<div
				key={step.id ?? step.internalId}
				onClick={() => handleEditStep(step)}
				className={cnNode({ hasErrors })}
				style={{
					borderColor: hasErrors ? undefined : colors.values.borderLight,
					backgroundColor: isStart
						? colors.values.surface
						: colors.values.background,
				}}
			>
				<div className="flex items-center gap-3">
					<div
						className="flex items-center justify-center w-8 h-8 rounded-lg flex-shrink-0"
						style={{
							backgroundColor: colors.values.surface,
							color: colors.values.textSecondary,
						}}
					>
						<FontAwesomeIcon
							icon={iconForStep(step.stepType)}
							className="h-4 w-4"
						/>
					</div>
					<div className="min-w-0 flex-1">
						<div
							className="text-sm font-semibold truncate"
							style={{ color: colors.values.textPrimary }}
						>
							{t(isStart ? 'Audience enters here' : label)}
						</div>
						{!isStart && step.stepName !== label && (
							<div
								className="text-xs truncate"
								style={{ color: colors.values.textMuted }}
							>
								{t(step.stepName)}
							</div>
						)}
						{hasErrors && (
							<div className="text-xs mt-0.5 text-danger">
								{t(`${errors.length} issue(s) — click to fix`)}
							</div>
						)}
					</div>
					{!isStart && (
						<button
							type="button"
							onClick={(e) => {
								e.stopPropagation();
								handleDeleteStep(step);
							}}
							className="flex-shrink-0 p-1.5 rounded hover:bg-danger-tint"
							style={{ color: colors.values.textMuted }}
							aria-label={t('Delete step')}
						>
							<FontAwesomeIcon icon={faTrashCan} className="h-3 w-3" />
						</button>
					)}
				</div>
			</div>
		);
	};

	/**
	 * The inline step-config panel, rendered directly under a node when it is
	 * the selected step — the 2.0 builder edits a step in place rather than in
	 * a slide-over.
	 */
	const renderInlineConfig = (step: AutomationStep): React.ReactNode => {
		if (selectedStep == null || !showStepConfig) {
			return null;
		}
		const isSelected =
			(step.id != null && step.id === selectedStep.id) ||
			(step.internalId != null && step.internalId === selectedStep.internalId);
		if (!isSelected) {
			return null;
		}
		return (
			<div key={`cfg-${step.id ?? step.internalId}`} className="my-4">
				<StepConfigSidebar
					inline
					visible={showStepConfig}
					step={selectedStep}
					onCancel={() => {
						setShowStepConfig(false);
						setSelectedStep(null);
					}}
					onChange={handleStepConfigChange}
					allSteps={steps}
					flowId={flowId}
					versionId={selectedVersionId}
					stepTypeSchemas={stepTypeSchemas}
					buildStepContext={buildStepContext}
				/>
			</div>
		);
	};

	/**
	 * Render a sequence of steps and, for branch steps, their lanes —
	 * recursively. `branchContext` is the path of branch entries leading to
	 * this sequence (empty = main flow).
	 */
	const renderSequence = (
		sequence: Array<AutomationStep>,
		branchContext: Array<BranchContextEntry>,
		keyPrefix: string,
	): React.ReactNode => {
		const nodes: Array<React.ReactNode> = [];

		sequence.forEach((step, index) => {
			if (index > 0) {
				nodes.push(renderConnector(`${keyPrefix}-conn-${index}`));
			}
			nodes.push(renderNode(step, false));

			const inlineConfig = renderInlineConfig(step);
			if (inlineConfig != null) {
				nodes.push(inlineConfig);
			}

			// Branch steps render their lanes inline, indented.
			const lanes = getBranchLanes(step);
			if (lanes.length > 0 && step.id != null) {
				nodes.push(
					<div
						key={`${keyPrefix}-branches-${step.id}`}
						className="mt-2 space-y-3 pl-5 border-l-2"
						style={{ borderColor: colors.values.borderLight }}
					>
						{lanes.map((lane) => {
							const entryConn = connections.find(
								(c) => c.fromStepId === step.id && c.conditionKey === lane.key,
							);
							const laneSteps = getSequence(entryConn?.toStepId ?? null);
							const laneContext: Array<BranchContextEntry> = [
								...branchContext,
								{
									stepId: step.id as number,
									stepName: step.stepName,
									branchKey: lane.key,
									branchLabel: lane.label,
								},
							];
							return (
								<div key={`${keyPrefix}-lane-${lane.key}`}>
									<div
										className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold mb-2"
										style={{
											backgroundColor: colors.values.surface,
											color: colors.values.textSecondary,
										}}
									>
										{t(lane.label)}
									</div>
									<div className="space-y-0">
										{laneSteps.length > 0 &&
											renderSequence(
												laneSteps,
												laneContext,
												`${keyPrefix}-lane-${lane.key}`,
											)}
										{laneSteps.length > 0 &&
											renderConnector(`${keyPrefix}-lane-${lane.key}-addconn`)}
										{renderAddButton(
											`${keyPrefix}-lane-${lane.key}-add`,
											laneContext,
											laneSteps,
										)}
									</div>
								</div>
							);
						})}
					</div>,
				);
			}
		});

		return nodes;
	};

	// ── Render ───────────────────────────────────────────────────────────

	if (loading) {
		return (
			<div className="flex items-center justify-center h-64">
				<LoadingSpinner size="lg" />
			</div>
		);
	}

	return (
		<div className={embedded ? '' : 'p-6'}>
			{!embedded && (
				<div className="flex items-center justify-between mb-4">
					<h2
						className="text-lg font-semibold"
						style={{ color: colors.values.textPrimary }}
					>
						{t('Flow')}
					</h2>
					<Button
						variant="primary"
						onClick={() => void handleSaveDraft()}
						disabled={persistence.saving || !isFlowValid(validationResult)}
					>
						{persistence.saving ? t('Saving...') : t('Save draft')}
					</Button>
				</div>
			)}

			<div
				className="rounded-lg border p-6"
				style={{
					borderColor: colors.values.borderLight,
					backgroundColor: colors.values.surface,
				}}
			>
				<div className="max-w-xl mx-auto space-y-0">
					{/* Start node */}
					{startStep != null && renderNode(startStep, true)}
					{startStep != null && renderInlineConfig(startStep)}

					{/* Main sequence */}
					{mainSequence.length > 0 && (
						<>
							{renderConnector('main-start-conn')}
							{renderSequence(mainSequence, [], 'main')}
						</>
					)}

					{/* Add to the end of the main flow. The start step is the
					    attach anchor so the first step links from it. */}
					{renderConnector('main-add-conn')}
					{renderAddButton(
						'main-add',
						[],
						startStep != null ? [startStep, ...mainSequence] : mainSequence,
					)}
				</div>
			</div>

			{/* Step palette — reuses the existing selector modal */}
			<StepSelectorModal
				visible={addTarget != null}
				onCancel={() => setAddTarget(null)}
				onSelect={handleStepSelected}
			/>
		</div>
	);
};

/**
 * Public component. Wraps {@link FlowBuilderInner} in `forwardRef` so an
 * embedding host can drive save/commit imperatively via {@link FlowBuilderHandle}.
 */
export const FlowBuilder = forwardRef<FlowBuilderHandle, FlowBuilderProps>(
	function FlowBuilder(props, ref) {
		return <FlowBuilderInner {...props} builderRef={ref} />;
	},
);

// ── Style helpers ────────────────────────────────────────────────────────

function cnNode(opts: { hasErrors: boolean }): string {
	const base =
		'rounded-lg border px-3.5 py-3 transition-colors mx-auto w-full max-w-sm cursor-pointer hover:border-primary';
	return `${base} ${opts.hasErrors ? 'border-danger' : ''}`.trim();
}

function cnAdd(): string {
	return 'inline-flex items-center gap-1.5 px-3 py-1 rounded-full border border-dashed text-xs font-medium transition-colors hover:border-primary hover:text-primary';
}
